import React from "react";
import { PageHeader } from "@/components/ui-blocks/PageHeader";
import { SEO } from "@/lib/seo";

const POLICIES = {
  privacy: {
    title: "Privacy Policy",
    intro: "Your privacy matters. This policy describes what personal information CAF collects, why and how it is used.",
    body: [
      ["Information we collect", "Contact details you submit via volunteer, donation, contact and issue-report forms; system information (device, browser, IP) for security and analytics."],
      ["How we use information", "To acknowledge and act on your submissions, issue 80G receipts, send you newsletters (only if you subscribe), and improve our services."],
      ["Sharing", "We do not sell your data. We only share with statutory authorities where required by law, and with audit partners under strict confidentiality."],
      ["Your rights", "You may request access, correction, or deletion of your data by writing to privacy@caf.org.in."],
      ["Cookies", "We use minimal cookies for functionality and privacy-friendly analytics."],
    ],
  },
  terms: {
    title: "Terms of Use",
    intro: "These terms govern your use of the CAF website.",
    body: [
      ["Content", "All content is for informational purposes. CAF does not endorse any political party or candidate."],
      ["Contributions", "By submitting content, you confirm you have the right to share it and grant CAF a non-exclusive license to use it for civic-awareness purposes."],
      ["Prohibited", "No unlawful, defamatory, hateful, or misleading content is allowed on any CAF form."],
      ["Liability", "CAF is not liable for any losses arising from use of the site beyond mandatory statutory liability."],
    ],
  },
  refund: {
    title: "Refund Policy",
    intro: "Donations made in error can be refunded within 7 days, subject to verification.",
    body: [
      ["Eligibility", "Refunds are considered for demonstrable errors (duplicate amount, wrong amount) reported within 7 days from payment."],
      ["Process", "Write to donate@caf.org.in with the payment reference. Refunds are processed to the original payment method within 10 working days after verification."],
      ["80G impact", "If an 80G receipt has been issued, the receipt is voided and reissued for the correct amount."],
    ],
  },
  donation: {
    title: "Donation Policy",
    intro: "CAF is committed to full accountability for every donation received.",
    body: [
      ["Tagging", "Every donation is tagged to a program or campaign at the time of receipt."],
      ["Utilization", "Program-wise expense records are maintained and audited annually by an independent firm."],
      ["Reporting", "Impact reports and utilization certificates are published annually and shared with CSR partners quarterly."],
      ["Compliance", "CAF is 12A, 80G and CSR-1 registered. Donations from foreign sources are governed by FCRA (status: in progress)."],
    ],
  },
  accessibility: {
    title: "Accessibility Statement",
    intro: "CAF is committed to digital accessibility for all citizens, including persons with disabilities.",
    body: [
      ["Standard", "We aim to conform to WCAG 2.2 AA. We test with keyboard-only navigation, screen readers and reduced-motion preferences."],
      ["Ongoing improvements", "Alt text, semantic HTML, focus indicators, colour contrast, form labels and error messages are continuously audited."],
      ["Feedback", "Report accessibility issues to accessibility@caf.org.in — we will respond within 5 working days."],
      ["Alternatives", "Any content that is not fully accessible can be requested in an alternate format."],
    ],
  },
  "child-protection": {
    title: "Child Protection Policy",
    intro: "Every child working with or served by CAF has a right to a safe, respectful, and dignified environment.",
    body: [
      ["Zero tolerance", "CAF has zero tolerance for any form of abuse, neglect, exploitation or discrimination against children."],
      ["Screening", "All volunteers and staff working with children undergo mandatory screening and orientation."],
      ["Reporting", "Any suspected violation is reported to the Grievance Officer within 24 hours and to the appropriate authorities under POCSO Act, 2012."],
      ["Consent & imagery", "Photos of children are used only with the informed consent of their guardians. Identifying details are never published without explicit permission."],
    ],
  },
  "volunteer-policy": {
    title: "Volunteer Policy",
    intro: "CAF volunteers are the backbone of our work. This policy governs volunteering standards, safety and recognition.",
    body: [
      ["Eligibility", "Anyone above 15 years (with parental consent for under-18s) may volunteer."],
      ["Non-partisanship", "Volunteers commit to lawful, peaceful and non-partisan civic action while representing CAF."],
      ["Safety", "Field operations follow CAF safety guidelines; volunteers may withdraw from any activity that feels unsafe."],
      ["Recognition", "Volunteers earn recognition badges, and are considered for annual awards and paid Fellowships."],
      ["Code of Ethics", "All volunteers agree to abide by the CAF Code of Ethics and Child Protection Policy."],
    ],
  },
  "code-of-ethics": {
    title: "Code of Ethics",
    intro: "Our Code of Ethics guides every CAF member, volunteer, staff and board member.",
    body: [
      ["Integrity", "We do what is right, not what is easy. Every rupee, every voucher, every report — transparent by default."],
      ["Non-partisanship", "We do not endorse any political party, ideology or candidate. Our positions are policy-focused, evidence-based and lawful."],
      ["Respect", "We respect the dignity of every person we serve or work with."],
      ["Confidentiality", "We protect the personal information of donors, volunteers and beneficiaries."],
      ["Accountability", "We accept our mistakes, publish learnings, and continuously improve."],
    ],
  },
  "conflict-of-interest": {
    title: "Conflict of Interest Policy",
    intro: "CAF requires all board members, staff and volunteers to disclose potential conflicts of interest and recuse themselves from related decisions.",
    body: [
      ["Disclosure", "All actual, potential or perceived conflicts of interest must be disclosed to the Board Secretary in writing."],
      ["Recusal", "The person disclosing a conflict is recused from all discussions and votes on the matter."],
      ["Record-keeping", "Disclosures and recusal decisions are recorded in board minutes and reviewed annually."],
      ["Family members", "Financial dealings with family members are prohibited without prior board approval."],
    ],
  },
};

export default function PolicyPage({ slug }) {
  const p = POLICIES[slug];
  if (!p) return <main className="container-wide py-24"><p>Policy not found.</p></main>;
  return (
    <main>
      <SEO title={p.title} description={p.intro} breadcrumbs={[{name: "Home", href:"/"},{name: p.title, href: "/"+slug}]} />
      <PageHeader eyebrow="Policy" title={p.title} lead={p.intro} />
      <section className="container-wide py-12 max-w-3xl">
        <div className="space-y-8">
          {p.body.map(([h, t]) => (
            <div key={h} className="rounded-2xl border border-border bg-background p-6">
              <h3 className="heading-3 text-xl">{h}</h3>
              <p className="text-[hsl(var(--muted-ink))] mt-2 leading-relaxed">{t}</p>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
}
